import { Calendar,Clock } from 'lucide-react'
import { memo } from 'react'

import ActionMenu from '@/components/ActionMenu'
import PlayOverlay from '@/components/PlayOverlay'
import StatusBadge from '@/components/StatusBadge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { formatTimeAgo } from '@/lib/time'
import { cn } from '@/lib/utils'
import type { Project } from '@/types/domain.types'

interface ProjectCardProps {
  project: Project
  onPlay: (project: Project) => void
  onRename: (project: Project) => void
  onCopyId: (project: Project) => void
  onDelete: (project: Project) => void
  onExport: (project: Project) => void
  onDownload: (project: Project) => void
  onEdit: (project: Project) => void
}

export const ProjectCard = memo(function ProjectCard({
  project,
  onPlay,
  onRename,
  onCopyId,
  onDelete,
  onExport,
  onDownload,
  onEdit,
}: ProjectCardProps) {
  const isComplete = project.status === 'complete' && project.renderUrl

  return (
    <Card className="border-gray-100 h-full overflow-visible">
      <CardContent className="flex flex-col h-full p-0">
        {/* Cover */}
        <div
          className={cn(
            'w-full aspect-[9/16] bg-black flex items-center justify-center',
            'cursor-pointer relative overflow-hidden',
            'hover:bg-gray-800 transition-colors'
          )}
          onClick={() => onPlay(project)}
        >
          {project.coverUrl && (
            <img
              src={project.coverUrl}
              alt={project.name}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}

          <PlayOverlay size="sm" />

          <div className="absolute top-1 left-1 z-20">
            <StatusBadge
              status={
                project.status === 'complete'
                  ? 'ready'
                  : project.status === 'rendering'
                  ? 'processing'
                  : 'draft'
              }
              size="sm"
            />
          </div>
        </div>

        {/* Info */}
        <div className="flex-1 flex flex-col justify-between p-2">
          <div className="flex items-start justify-between gap-0.5 mb-2">
            <h3
              className="text-sm font-semibold text-gray-900 line-clamp-2 flex-1 leading-tight"
              title={project.name}
            >
              {project.name}
            </h3>

            <ActionMenu
              items={[
                { label: 'Rename', onClick: () => onRename(project) },
                { label: 'Copy ID', onClick: () => onCopyId(project) },
                { label: 'Delete', onClick: () => onDelete(project), variant: 'destructive' },
              ]}
            />
          </div>

          <div>
            <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
              <div className="flex items-center">
                <Clock className="w-3 h-3 mr-1 text-gray-400" />
                <span>{project.duration}</span>
              </div>
              <div className="flex items-center">
                <Calendar className="w-3 h-3 mr-1 text-gray-400" />
                <span>{formatTimeAgo(project.createdAt)}</span>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                className={cn(
                  "w-full text-xs px-3 disabled:opacity-50",
                  isComplete
                    ? "bg-black text-white hover:bg-gray-800"
                    : "bg-white text-gray-900 hover:bg-gray-50 border border-gray-200"
                )}
                size="sm"
                disabled={project.status === 'rendering'}
                onClick={() => (isComplete ? onDownload(project) : onExport(project))}
              >
                {project.status === 'rendering'
                  ? 'Exporting...'
                  : isComplete
                  ? 'Download'
                  : 'Export'}
              </Button>
              <Button
                className="w-full text-xs px-3 bg-gray-900 text-white hover:bg-gray-800"
                size="sm"
                onClick={() => onEdit(project)}
              >
                Open Editor
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
})

